import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft, Play, Send, RotateCcw, CheckCircle2, XCircle, AlertTriangle,
  Loader2, Lightbulb, Sparkles, X, MessageSquare, Trophy, Flame, Lock, Bug, Layers
} from "lucide-react";
import { getStoredUser, getAuthConfig, loginWithGoogle } from "../lib/userAuth";
import { ensurePyodide, runCode, runTests } from "../lib/pyodideRunner";
import ModuleUnlockCelebration from "../components/ModuleUnlockCelebration";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VERDICT_STYLES = {
  PASS: { bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30", icon: CheckCircle2, label: "PASS" },
  PARTIAL: { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30", icon: AlertTriangle, label: "PARTIAL" },
  FAIL: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30", icon: XCircle, label: "FAIL" },
  ERROR: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30", icon: XCircle, label: "ERROR" },
};

function VerdictBanner({ result }) {
  if (!result) return null;
  const v = VERDICT_STYLES[result.verdict] || VERDICT_STYLES.FAIL;
  const Icon = v.icon;
  return (
    <div className={`flex items-center gap-3 px-4 py-3 border rounded-lg ${v.bg} ${v.border}`}>
      <Icon size={20} className={v.text} />
      <div className="flex-1 min-w-0">
        <div className={`font-outfit font-bold ${v.text}`}>{v.label}</div>
        <div className="text-xs text-zinc-400 font-dm-sans">
          {result.passedAll}/{result.totalAll} tests passed · {result.durationMs}ms
        </div>
      </div>
    </div>
  );
}

function TestResultsList({ result }) {
  if (!result) return null;
  const all = [...(result.visible?.results || []).map(r => ({ ...r, kind: "visible" })), ...(result.hidden?.results || []).map(r => ({ ...r, kind: "hidden" }))];
  if (all.length === 0) return null;
  return (
    <div className="space-y-1.5">
      {all.map((r, i) => (
        <div key={i} className="flex items-start gap-2 text-xs font-mono">
          {r.passed ? (
            <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
          ) : (
            <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <span className={r.passed ? "text-emerald-300" : "text-red-300"}>
              {r.kind === "hidden" ? "[hidden] " : ""}{r.name}
            </span>
            {!r.passed && r.message && (
              <pre className="text-zinc-500 whitespace-pre-wrap mt-0.5 text-[11px] leading-snug max-h-24 overflow-y-auto">
                {r.message}
              </pre>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function TutorPanel({ open, onClose, lessonId, code, lastFailures, onCallsRemaining }) {
  const [hintLevel, setHintLevel] = useState(1);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [callsRemaining, setCallsRemaining] = useState(null);

  const askTutor = async (level, mode = "hint") => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/python-lab/tutor`, {
        lesson_id: lessonId,
        code,
        level,
        mode,
        test_failures: lastFailures || ""
      }, getAuthConfig());
      setMessages((m) => [...m, { role: "user", content: `Level ${level} ${mode}` }, { role: "tutor", content: r.data.reply, level }]);
      setCallsRemaining(r.data.calls_remaining_today);
      onCallsRemaining?.(r.data.calls_remaining_today);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Tutor error";
      setMessages((m) => [...m, { role: "tutor", content: `_${detail}_`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.25 }}
          className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-[#0b0d12] border-l border-zinc-800 z-50 flex flex-col"
          data-testid="tutor-panel"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-to-br from-[#00F0FF] to-[#7000FF] rounded-lg flex items-center justify-center">
                <Sparkles size={16} className="text-black" />
              </div>
              <div>
                <div className="font-outfit font-bold text-white text-sm">PyForge Tutor</div>
                <div className="text-[10px] text-zinc-500 font-dm-sans">Claude Sonnet 4.5</div>
              </div>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-white p-1" data-testid="tutor-close-btn">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {messages.length === 0 && !loading && (
              <div className="text-center pt-10">
                <Lightbulb className="text-[#FFC857] mx-auto mb-3" size={32} />
                <h3 className="font-outfit font-bold text-white text-base mb-2">Need a hint?</h3>
                <p className="text-zinc-400 text-xs font-dm-sans mb-6 max-w-xs mx-auto leading-relaxed">
                  Ask for a hint at the right level. Level 1 nudges, Level 4 reveals the full solution.
                </p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                {m.role === "user" ? (
                  <div className="bg-[#00F0FF] text-black px-3 py-1.5 rounded-lg text-xs font-dm-sans font-bold">
                    {m.content}
                  </div>
                ) : (
                  <div className={`bg-zinc-900 border rounded-lg p-4 ${m.error ? "border-red-500/30" : "border-zinc-800"}`}>
                    {m.level && <div className="text-[10px] font-mono text-[#00F0FF] mb-2">HINT LEVEL {m.level}</div>}
                    <div className="prose prose-invert prose-sm max-w-none font-dm-sans leading-relaxed">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                        code: ({ inline, children }) =>
                          inline ? <code className="bg-zinc-800 text-[#00F0FF] px-1 py-0.5 rounded text-[12px]">{children}</code>
                                 : <code className="block bg-zinc-950 border border-zinc-800 rounded p-3 text-xs whitespace-pre overflow-x-auto text-zinc-200">{children}</code>
                      }}>{m.content}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin text-[#00F0FF]" />
                <span className="text-zinc-400 text-xs font-dm-sans">Tutor is thinking...</span>
              </div>
            )}
          </div>

          <div className="border-t border-zinc-800 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-dm-sans text-zinc-500">Hint Level</span>
              {callsRemaining !== null && (
                <span className="text-[10px] font-dm-sans text-zinc-600">{callsRemaining} calls left today</span>
              )}
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((lvl) => (
                <button
                  key={lvl}
                  data-testid={`hint-level-${lvl}`}
                  onClick={() => { setHintLevel(lvl); askTutor(lvl); }}
                  disabled={loading}
                  className={`py-2 rounded-md text-xs font-bold font-dm-sans transition-all ${
                    hintLevel === lvl
                      ? "bg-[#00F0FF] text-black"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  } disabled:opacity-50`}
                >
                  L{lvl}
                </button>
              ))}
            </div>
            <button
              data-testid="tutor-explain-error"
              onClick={() => askTutor(1, "explain_error")}
              disabled={loading || !lastFailures}
              className="w-full py-2 bg-zinc-900 text-zinc-300 hover:text-white text-xs font-bold font-dm-sans rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Explain my error
            </button>
            <p className="text-[10px] text-zinc-600 font-dm-sans leading-snug">
              L1 = guiding question · L2 = concept name · L3 = pseudo-code · L4 = full solution
            </p>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

export default function PythonLessonPage() {
  const { lessonSlug } = useParams();
  const navigate = useNavigate();
  const user = getStoredUser();

  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("# loading...\n");
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pyodideStatus, setPyodideStatus] = useState("loading");
  const [output, setOutput] = useState({ stdout: "", stderr: "", error: null });
  const [testResult, setTestResult] = useState(null);
  const [tutorOpen, setTutorOpen] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [xpToast, setXpToast] = useState(null);
  const [celebration, setCelebration] = useState(null); // { moduleTitle, nextModule } | null
  const editorRef = useRef(null);
  const outputPanelRef = useRef(null);

  // Auto-scroll output into view whenever new output arrives
  useEffect(() => {
    if (output && (output.stdout || output.stderr || output.error)) {
      outputPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [output]);
  useEffect(() => {
    if (testResult) {
      outputPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [testResult]);

  // Pyodide init
  useEffect(() => {
    setPyodideStatus("loading");
    ensurePyodide(setPyodideStatus).then(() => setPyodideStatus("ready")).catch(() => setPyodideStatus("error"));
  }, []);

  // Lesson load
  useEffect(() => {
    if (!user?.session_token) {
      loginWithGoogle(`/ai-tools/python-lab/lesson/${lessonSlug}`);
      return;
    }
    setLoading(true);
    axios.get(`${API}/python-lab/lessons/${lessonSlug}`, getAuthConfig())
      .then((r) => {
        setLesson(r.data);
        setCode(r.data.starter_code || "# Your code here\n");
      })
      .catch((e) => {
        if (e?.response?.status === 401) loginWithGoogle(`/ai-tools/python-lab/lesson/${lessonSlug}`);
        else if (e?.response?.status === 402) {
          // Premium required — redirect to pricing
          window.location.href = "/pricing#python-lab";
        }
        console.error(e);
      })
      .finally(() => setLoading(false));
  }, [lessonSlug, user?.session_token]);

  const handleRun = useCallback(async () => {
    if (pyodideStatus !== "ready") return;
    setRunning(true);
    setOutput({ stdout: "", stderr: "", error: null });
    setTestResult(null);
    try {
      const r = await runCode(code);
      setOutput(r);
    } finally {
      setRunning(false);
    }
  }, [code, pyodideStatus]);

  const handleSubmit = useCallback(async () => {
    if (!lesson || pyodideStatus !== "ready") return;
    setSubmitting(true);
    setTestResult(null);
    try {
      // Need both visible and hidden tests — fetch hidden tests via separate endpoint? No: hidden are stripped from public.
      // We need to send code to the BACKEND for hidden test grading. Since we keep tests in DB only (not pushed to client),
      // we can run visible tests client-side AND call backend to run hidden tests... but the PRD specifies CLIENT-SIDE eval.
      //
      // Compromise: server returns visible_tests in response (already does), and we also fetch the lesson's hidden tests
      // via an authenticated /grade endpoint that runs hidden tests SERVER-SIDE? That defeats Pyodide.
      //
      // Actual solution: backend exposes hidden tests for grading run client-side under a "graded" endpoint that also
      // logs the attempt. We'll fetch visible+hidden together via /grade.
      //
      // Simpler: include `hidden_tests` in the lesson detail for authenticated users (since the user must run them
      // anyway). But that exposes them to view-source — acceptable trade-off for Phase 1.
      const visibleTests = lesson.visible_tests || "";
      // For Phase 1 we re-fetch the hidden test bundle from backend at submit time (so they aren't in initial page load)
      let hiddenTests = "";
      try {
        const gradeRes = await axios.get(`${API}/python-lab/lessons/${lessonSlug}/grade-bundle`, getAuthConfig());
        hiddenTests = gradeRes.data?.hidden_tests || "";
      } catch {
        // fallthrough — grade with visible only
      }

      const result = await runTests({ code, visibleTests, hiddenTests });
      setTestResult(result);
      setOutput((o) => ({ ...o, stdout: result.stdout || o.stdout }));

      // Submit attempt
      const sub = await axios.post(`${API}/python-lab/submissions`, {
        lesson_id: lesson.lesson_id,
        code,
        verdict: result.verdict,
        tests_passed: result.passedAll,
        tests_total: result.totalAll,
        duration_ms: result.durationMs,
        hints_used: hintsUsed
      }, getAuthConfig());

      if (sub.data.xp_awarded > 0) {
        setXpToast({ xp: sub.data.xp_awarded, levelUp: sub.data.level_up, streak: sub.data.streak });
        setTimeout(() => setXpToast(null), 4000);
      }

      // 🎉 Celebrate when this submission completes the entire module
      if (sub.data.module_completed) {
        // Slight delay so the verdict banner registers first, then the party kicks in
        setTimeout(() => {
          setCelebration({
            moduleId: lesson.module_id || lesson.module?.module_id || null,
            moduleTitle: sub.data.module_title,
            nextModule: sub.data.next_module || null
          });
        }, 700);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  }, [code, lesson, lessonSlug, hintsUsed, pyodideStatus]);

  const handleReset = () => {
    if (lesson?.starter_code) setCode(lesson.starter_code);
    setOutput({ stdout: "", stderr: "", error: null });
    setTestResult(null);
  };

  const lastFailures = (testResult?.visible?.results || [])
    .concat(testResult?.hidden?.results || [])
    .filter((r) => !r.passed)
    .map((r) => `${r.name}: ${r.message}`)
    .join("\n");

  if (!user?.session_token) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
      </div>
    );
  }
  if (loading || !lesson) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  // Prerequisite gate (project step lessons)
  const prereq = lesson.prerequisite;
  if (prereq && !prereq.completed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 py-20">
        <div className="max-w-md w-full bg-zinc-900/80 border border-zinc-800 rounded-2xl p-10 text-center">
          <div className="w-14 h-14 bg-amber-500/15 border border-amber-500/30 rounded-xl flex items-center justify-center mx-auto mb-5">
            <Lock size={22} className="text-amber-400" />
          </div>
          <h1 className="font-outfit font-bold text-2xl mb-2">Locked</h1>
          <p className="text-sm text-zinc-400 font-dm-sans mb-6 leading-relaxed">
            Complete <span className="text-white font-bold">"{prereq.lesson?.title || 'the previous step'}"</span> before unlocking this one.
          </p>
          <div className="flex flex-col gap-2">
            <button
              data-testid="goto-prereq-btn"
              onClick={() => navigate(`/ai-tools/python-lab/lesson/${prereq.lesson?.slug}`)}
              className="bg-[#00F0FF] text-black px-5 py-2.5 rounded-lg text-sm font-dm-sans font-bold hover:bg-white transition-colors"
            >
              Go to previous step
            </button>
            <button
              onClick={() => navigate(`/ai-tools/python-lab/module/${lesson.module?.slug}`)}
              className="text-zinc-400 hover:text-white text-xs font-dm-sans transition-colors mt-1"
            >
              Back to module
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-zinc-900 bg-[#050505]/85 backdrop-blur-xl flex-shrink-0">
        <div className="max-w-[1600px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <button
            data-testid="back-to-module"
            onClick={() => navigate(`/ai-tools/python-lab/module/${lesson.module?.slug || "python-basics"}`)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-dm-sans transition-colors flex-shrink-0"
          >
            <ArrowLeft size={16} /> {lesson.module?.title || "Module"}
          </button>
          <h1 className="font-outfit font-bold text-white text-base truncate flex-1 text-center hidden md:block">
            {lesson.title}
          </h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="open-tutor-btn"
              onClick={() => { setTutorOpen(true); setHintsUsed((h) => h + 1); }}
              className="flex items-center gap-1.5 bg-gradient-to-r from-[#00F0FF]/20 to-[#7000FF]/20 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10 px-3 py-1.5 rounded-md text-xs font-dm-sans font-bold transition-colors"
            >
              <Sparkles size={13} /> AI Tutor
            </button>
          </div>
        </div>
      </header>

      {/* Body — split screen */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* LEFT: Lesson markdown */}
        <div className="lg:w-[42%] lg:border-r border-zinc-900 overflow-y-auto px-6 py-8 bg-[#070709]">
          <div className="max-w-prose">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-[10px] font-mono font-bold text-[#00F0FF]">
                LESSON {String(lesson.order || 0).padStart(2, "0")} · +{lesson.xp_reward || 10} XP
              </span>
              {lesson.kind === "debug" && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-amber-400 bg-amber-400/10 border border-amber-400/30 px-1.5 py-0.5 rounded">
                  <Bug size={10} /> DEBUG · {(lesson.bug_category || '').toUpperCase()}
                </span>
              )}
              {lesson.kind === "project_step" && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold text-purple-400 bg-purple-400/10 border border-purple-400/30 px-1.5 py-0.5 rounded">
                  <Layers size={10} /> STEP {lesson.step_number}
                </span>
              )}
            </div>
            <div className="prose prose-invert max-w-none font-dm-sans">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h1 className="font-outfit font-black text-3xl text-white mb-5">{children}</h1>,
                  h2: ({ children }) => <h2 className="font-outfit font-bold text-xl text-white mt-7 mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="font-outfit font-bold text-lg text-white mt-5 mb-2">{children}</h3>,
                  p: ({ children }) => <p className="text-zinc-300 leading-relaxed mb-4">{children}</p>,
                  code: ({ inline, children }) =>
                    inline
                      ? <code className="bg-zinc-800 text-[#00F0FF] px-1.5 py-0.5 rounded text-[13px] font-mono">{children}</code>
                      : <code className="block bg-zinc-950 border border-zinc-800 rounded-lg p-4 text-sm font-mono whitespace-pre overflow-x-auto text-zinc-200 my-3 leading-relaxed">{children}</code>,
                  ul: ({ children }) => <ul className="list-disc list-inside space-y-1.5 text-zinc-300 mb-4">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside space-y-1.5 text-zinc-300 mb-4">{children}</ol>,
                  table: ({ children }) => <div className="overflow-x-auto my-4"><table className="border border-zinc-800 text-sm">{children}</table></div>,
                  th: ({ children }) => <th className="border border-zinc-800 px-3 py-1.5 bg-zinc-900 text-zinc-300 font-dm-sans font-bold text-left">{children}</th>,
                  td: ({ children }) => <td className="border border-zinc-800 px-3 py-1.5 text-zinc-300 font-dm-sans">{children}</td>,
                  blockquote: ({ children }) => <blockquote className="border-l-2 border-[#00F0FF] pl-4 my-4 text-zinc-400 italic">{children}</blockquote>,
                }}
              >{lesson.body_md || ""}</ReactMarkdown>
            </div>
          </div>
        </div>

        {/* RIGHT: Editor + output */}
        <div className="lg:w-[58%] flex flex-col bg-[#0b0d12] min-h-0">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-900 bg-[#0b0d12] flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-zinc-500">solution.py</span>
              {pyodideStatus !== "ready" && (
                <span className="text-[10px] font-dm-sans text-amber-400 inline-flex items-center gap-1">
                  <Loader2 size={10} className="animate-spin" /> Loading Python runtime...
                </span>
              )}
              {pyodideStatus === "ready" && (
                <span className="text-[10px] font-dm-sans text-emerald-400">● Python 3.12 ready</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="reset-btn"
                onClick={handleReset}
                className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white px-3 py-1.5 rounded text-xs font-dm-sans font-bold transition-colors"
              >
                <RotateCcw size={12} /> Reset
              </button>
              <button
                data-testid="run-btn"
                onClick={handleRun}
                disabled={running || pyodideStatus !== "ready"}
                className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-1.5 rounded text-xs font-dm-sans font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                Run
              </button>
              <button
                data-testid="submit-btn"
                onClick={handleSubmit}
                disabled={submitting || pyodideStatus !== "ready"}
                className="flex items-center gap-1.5 bg-[#00F0FF] hover:bg-white text-black px-4 py-1.5 rounded text-xs font-dm-sans font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Submit
              </button>
            </div>
          </div>

          {/* Editor */}
          <div className="flex-1 min-h-0 relative" style={{ minHeight: "320px" }}>
            <Editor
              height="100%"
              language="python"
              theme="vs-dark"
              value={code}
              onChange={(v) => setCode(v ?? "")}
              onMount={(ed) => { editorRef.current = ed; }}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                fontFamily: 'JetBrains Mono, Menlo, monospace',
                tabSize: 4,
                insertSpaces: true,
                automaticLayout: true,
                scrollBeyondLastLine: false,
                renderLineHighlight: "line",
                lineNumbers: "on",
              }}
            />
          </div>

          {/* Output panel */}
          <div
            ref={outputPanelRef}
            className="border-t border-zinc-900 bg-[#070709] flex-shrink-0 max-h-[45%] min-h-[140px] overflow-y-auto"
          >
            <div className="px-4 py-3 space-y-3">
              {testResult && <VerdictBanner result={testResult} />}
              {output.error && !testResult && (
                <div className="flex items-start gap-3 px-4 py-3 border rounded-lg bg-red-500/15 border-red-500/30">
                  <XCircle size={18} className="text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-outfit font-bold text-red-400 text-sm">Error while running your code</div>
                    <pre className="mt-1 text-xs text-red-300 font-mono whitespace-pre-wrap leading-relaxed">{output.error}</pre>
                    <div className="text-[11px] text-zinc-500 font-dm-sans mt-1.5">
                      Tip: Python is case-sensitive. <code className="text-[#00F0FF]">print</code> works, but <code className="text-amber-300">Print</code> doesn't.
                    </div>
                  </div>
                </div>
              )}
              {(output.stdout || output.stderr) && (
                <div>
                  <div className="text-[10px] font-mono text-zinc-500 mb-1">OUTPUT</div>
                  <pre className="bg-black/50 border border-zinc-900 rounded p-3 text-xs font-mono text-zinc-200 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {output.stdout && <span className="text-zinc-200">{output.stdout}</span>}
                    {output.stderr && <span className="text-amber-300">{output.stdout ? "\n" : ""}{output.stderr}</span>}
                  </pre>
                </div>
              )}
              {testResult && (
                <div>
                  <div className="text-[10px] font-mono text-zinc-500 mb-1">TESTS</div>
                  <TestResultsList result={testResult} />
                </div>
              )}
              {!testResult && !output.stdout && !output.stderr && !output.error && (
                <div className="text-xs text-zinc-600 font-dm-sans py-2">
                  Click <span className="text-zinc-400">Run</span> to execute, or <span className="text-[#00F0FF]">Submit</span> to grade your solution.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* XP toast */}
      <AnimatePresence>
        {xpToast && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="fixed top-20 left-1/2 -translate-x-1/2 bg-zinc-900 border border-emerald-500/40 rounded-xl px-5 py-3 flex items-center gap-3 shadow-2xl z-40"
            data-testid="xp-toast"
          >
            <Trophy size={20} className="text-amber-400" />
            <div>
              <div className="font-outfit font-bold text-white text-sm">+{xpToast.xp} XP</div>
              {xpToast.levelUp && <div className="text-[10px] font-dm-sans text-[#00F0FF]">Level up!</div>}
              {xpToast.streak && <div className="text-[10px] font-dm-sans text-amber-400 inline-flex items-center gap-1"><Flame size={10} /> {xpToast.streak.current}-day streak</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <TutorPanel
        open={tutorOpen}
        onClose={() => setTutorOpen(false)}
        lessonId={lesson.lesson_id}
        code={code}
        lastFailures={lastFailures}
      />

      <ModuleUnlockCelebration
        open={!!celebration}
        moduleId={celebration?.moduleId}
        moduleTitle={celebration?.moduleTitle}
        nextModule={celebration?.nextModule}
        onClose={() => setCelebration(null)}
        onContinue={() => {
          const slug = celebration?.nextModule?.slug;
          setCelebration(null);
          if (slug) navigate(`/ai-tools/python-lab/module/${slug}`);
        }}
      />
    </div>
  );
}
